"use client";

import NextLink from "next/link";
import { useRef, type MouseEvent } from "react";

import { formatBrlInteger } from "@/lib/host-panel/money";
import { packLabel, packTotalCents, type ListedPiece } from "@/lib/host-panel/catalog-types";
import { pieceActionLabel } from "@/lib/host-panel/piece-action-label";
import { TRANSPARENT_PIXEL, resolvePieceImageUrl } from "@/lib/host-panel/piece-image";
import { desiredUnitsLabel } from "@/lib/suggestion-lists/suggestion-lists";
import { cn } from "@/lib/utils";

/**
 * Catalog piece card (handoff §7.1), showing the **pack total** and never the unit price
 * (canon §10).
 *
 * The prototype makes the whole card a clickable `div` and cancels propagation in every inner
 * control. Here the card opens the piece too, **through** a real link on the name: a bare `div`
 * with `onClick` is unreachable by keyboard, cannot open in a new tab, and announces nothing.
 */

export type PieceCardProps = {
  /** Called when the card opens its piece (a press on the name or anywhere on the card). */
  onOpen?: () => void;
  piece: ListedPiece;
  /** Target for the piece's detail; null while that page does not exist. */
  href: string | null;
  /** Primary action under the card: opens the quantity and pack dialog. */
  /**
   * Opens the pack dialog. Absent when the list cannot be changed — a closed list, or the
   * (`list-write-access.ts`): the button stays where the design puts it, reading
   * the same label, and is disabled, as the guest's own CTA is on a closed list.
   */
  onOpenPack?: (piece: ListedPiece, trigger: HTMLElement | null) => void;
  /** Bulk marking. Present only in add scope. */
  selection?: { isSelected: boolean; onToggle: (piece: ListedPiece) => void };
  /** Featured star. Present only in list scope, and never alongside `guestPresenting`. */
  featuring?: { onToggle: (piece: ListedPiece) => void };
  /**
   * Guest preview (requirement 1.17, `logic/01.js:718,753-755`): swaps the card's action for
   * "Presentear", the reference's `ctaDisplay`/`ctaLabel`, and hides the star and the progress
   * bar the same way the reference's `destDisplay`/`progDisplay` do for `ctx()==='convidado'`.
   */
  guestPresenting?: {
    onPresent: (piece: ListedPiece) => void;
    /** A closed list takes no more gifts: the action reads so and is off (`logic/01.js:754-755`). */
    isClosed?: boolean;
    /**
     * Whoever is looking cannot buy here — today, the host on their own list. The action gives
     * way to the design's locked row ("Compra bloqueada" with a padlock), which the prototype
     * draws for the same situation in support mode.
     */
    isBlocked?: boolean;
    /** This piece's add is in flight — the checkout branch's own per-piece busy state. */
    isAdding?: boolean;
    /** Some add is in flight: every action waits, so two taps never race the same bag. */
    isAnyAdding?: boolean;
  };
  /**
   * A piece of a house suggestion list (the design's `semPreco`): no price, no progress and no
   * action — the card reads the collection, the name and how many units the house suggests.
   */
  isSuggestion?: boolean;
  /**
   * The creation's curation (canon §10, `curadoria`): the corner box and the card's action both
   * put the piece in the list or take it out, at once — "Adicionar à lista", "Na sua lista".
   */
  curation?: { onToggle: (piece: ListedPiece) => void };
};

/** The card's single tag, in the canon's precedence order. */
function pieceTag(piece: ListedPiece, isAddScope: boolean, isGuest: boolean) {
  if (isAddScope && piece.inList) {
    return { label: "Adicionado", tone: "green" as const, dot: true };
  }

  // The met goal is the host's reading (`teto = cheio && anfitriao` in the prototype): a guest
  // sees the featured tag, or none.
  if (!isAddScope && !isGuest && piece.purchased >= piece.total) {
    return { label: "Meta atingida", tone: "gilt" as const, dot: false, check: true };
  }

  if (!isAddScope && piece.featured) {
    return { label: "Destaque", tone: "green" as const, dot: false, star: true };
  }

  return null;
}

const TAG_TONE = {
  green: "text-[var(--tb-green-deep)] shadow-[inset_0_0_0_1px_var(--tb-bulhoes-green)]",
  gilt: "text-[var(--tb-gilt)] shadow-[inset_0_0_0_1px_var(--tb-gilt)]",
} as const;

export function PieceCard({
  piece,
  href,
  onOpen,
  onOpenPack,
  selection,
  featuring,
  guestPresenting,
  isSuggestion = false,
  curation,
}: PieceCardProps) {
  const isAddScope = selection !== undefined || curation !== undefined;
  const isGuest = guestPresenting !== undefined;
  const tag = pieceTag(piece, isAddScope, isGuest);
  const hasMetGoal = piece.purchased >= piece.total;
  const imageUrl = resolvePieceImageUrl(piece.imageKey);
  const nameLink = useRef<HTMLAnchorElement>(null);

  /** The corner box: bulk marking in the panel's add scope, or the piece itself in the curation. */
  const corner =
    curation !== undefined
      ? {
          isSelected: piece.inList,
          label: piece.inList ? `Tirar ${piece.name} da lista` : `Adicionar ${piece.name} à lista`,
          onToggle: () => curation.onToggle(piece),
        }
      : selection !== undefined
        ? { isSelected: selection.isSelected, label: `Selecionar ${piece.name}`, onToggle: () => selection.onToggle(piece) }
        : null;

  /*
   * The prototype's whole card opens the piece (`abrirPdp` on the card's root). The name stays a
   * real link — that is what the keyboard and a screen reader reach — and a click anywhere else
   * on the card follows that same link, so there is one destination and one way to declare it.
   * The card's own controls (select, feature, edit pack, present) keep their action.
   */
  function openFromCard(event: MouseEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (target.closest("a, button, input, label, select, textarea") !== null) return;

    nameLink.current?.click();
  }

  return (
    <div
      data-piece-card=""
      onClick={href === null ? undefined : openFromCard}
      // `flex-1`: the card fills its grid item, so the action below (`mt-auto`) reaches the foot.
      className={cn("flex flex-1 flex-col px-2", href !== null && "cursor-pointer")}
    >
      <div
        data-piece-frame=""
        data-tone={piece.tone}
        className={cn(
          "tb-piece-frame relative -mx-2 aspect-[4/5] overflow-hidden",
          // A met goal is celebration, not exhaustion: the piece earns the gilt ring.
          hasMetGoal && !isAddScope && !isGuest && "shadow-[inset_0_0_0_1px_var(--tb-gilt)]",
        )}
      >
        {/*
         * External photo host, not configured for the Next optimizer. A failure falls back to
         * the transparent pixel and the frame does not move.
         */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl ?? TRANSPARENT_PIXEL}
          alt=""
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = TRANSPARENT_PIXEL;
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />

        {tag !== null ? (
          <span
            className={cn(
              "absolute left-3 top-3 inline-flex items-center gap-[7px] whitespace-nowrap rounded-[var(--tb-radius-pill)] bg-tb-white px-3 py-1.5 text-[12px] font-medium uppercase leading-[1.3] tracking-[0.1em]",
              TAG_TONE[tag.tone],
            )}
          >
            {tag.label}
            {tag.dot ? (
              <span aria-hidden="true" className="size-1.5 flex-none rounded-full bg-tb-green" />
            ) : null}
            {tag.star ? (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="flex-none"
              >
                <path d="M12 4.5l2.3 4.9 5.2.7-3.8 3.7.9 5.2-4.6-2.6-4.6 2.6.9-5.2L4.5 10l5.2-.7Z" />
              </svg>
            ) : null}
            {tag.check ? (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="flex-none"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : null}
          </span>
        ) : null}

        {corner !== null ? (
          <button
            type="button"
            aria-pressed={corner.isSelected}
            aria-label={corner.label}
            onClick={corner.onToggle}
            className={cn(
              /*
               * The visible square stays the prototype's; what reaches `--tb-touch-min` is the
               * area that accepts the tap. In a grid of cards a near miss lands on the photo,
               * which is not actionable. `before:` creates the area without touching the flow.
               */
              // 4px + the 6px between the 44px target and its 32px square: the prototype's 10px.
              "absolute right-1 top-1 grid size-[var(--tb-touch-min)] cursor-pointer place-content-center",
              "before:absolute before:inset-0 before:m-auto before:size-8 before:border before:content-['']",
              "[&>svg]:relative",
              "transition-[background-color,border-color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]",
              "before:transition-[background-color,border-color] before:duration-[var(--tb-duration-2)] before:ease-[var(--tb-ease-linen)]",
              corner.isSelected
                ? "before:border-tb-green before:bg-tb-green"
                : "before:border-[var(--tb-border)] before:bg-[rgb(255_255_255_/_0.86)]",
            )}
          >
            {corner.isSelected ? (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--tb-porcelain)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : null}
          </button>
        ) : null}

        {featuring !== undefined && guestPresenting === undefined ? (
          <button
            type="button"
            aria-pressed={piece.featured}
            aria-label={
              piece.featured ? `Retirar destaque de ${piece.name}` : `Destacar ${piece.name}`
            }
            onClick={() => featuring.onToggle(piece)}
            className={cn(
              // Same device as the selector above: small disc, full touch target.
              // The 36px disc sits where the prototype draws it — 10px from the top and the right. The
              // 44px target around it is 4px larger on each side, hence the 6px.
              "absolute right-[6px] top-[6px] grid size-[var(--tb-touch-min)] cursor-pointer place-content-center",
              "before:absolute before:inset-0 before:m-auto before:size-9 before:rounded-full before:content-['']",
              "[&>svg]:relative",
              "transition-opacity duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:opacity-70",
              "before:transition-colors before:duration-[var(--tb-duration-2)] before:ease-[var(--tb-ease-linen)]",
              piece.featured ? "before:bg-tb-green" : "before:bg-[rgb(255_255_255_/_0.86)]",
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={piece.featured ? "var(--tb-porcelain)" : "none"}
              stroke={piece.featured ? "var(--tb-porcelain)" : "var(--fg)"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 4.5l2.3 4.9 5.2.7-3.8 3.7.9 5.2-4.6-2.6-4.6 2.6.9-5.2L4.5 10l5.2-.7Z" />
            </svg>
          </button>
        ) : null}
      </div>

      <span className="mt-3.5 min-h-[15px] text-[10px] uppercase leading-[1.4] tracking-[0.16em] text-[var(--fg-subtle)]">
        {piece.collection}
      </span>

      <span data-piece-name="" className="mt-1.5 line-clamp-2 text-[14px] leading-[1.35] text-[var(--fg)]">
        {href === null ? (
          piece.name
        ) : (
          // `NextLink`, not a plain `<a>`: a plain anchor reloads the whole document, skipping the
          // router's prefetch and the page bridge's cover on the way out.
          <NextLink
            ref={nameLink}
            href={href}
            onClick={onOpen}
            className="transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:text-tb-green"
          >
            {piece.name}
          </NextLink>
        )}
      </span>

      {isSuggestion ? (
        <span className="mt-2 font-mono text-[11px] tracking-[0.05em] text-[var(--fg-subtle)]">
          {desiredUnitsLabel(piece.total)}
        </span>
      ) : (
        <span className="mt-2 flex items-baseline gap-1.5 font-mono text-[12px] text-[var(--fg)]">
          R$ {formatBrlInteger(packTotalCents(piece))}
          <span aria-hidden="true" className="text-[var(--fg-subtle)]">
            ·
          </span>
          <span className="font-sans text-[11px] text-[var(--fg-muted)]">{packLabel(piece)}</span>
        </span>
      )}

      {/* Progress only in list scope, and never in the guest preview: there the piece is not
       * being tracked toward a goal, it is being offered. */}
      {/* The prototype's rule is the piece, not the scope (`pr.naLista !== false`): a listed piece
          keeps its progress while the host adds others; a piece outside the list has no goal. */}
      {piece.inList && guestPresenting === undefined && !isSuggestion ? <PieceProgress piece={piece} /> : null}

      {isSuggestion ? null : (
        <span className="mt-auto pt-3.5">
          {guestPresenting?.isBlocked === true ? (
            /*
             * The one who cannot buy reads so, in the design's own row: the CTA gives way to a
             * bordered, muted line with a padlock (`markup.html:600`, the same block the piece's
             * page already draws in support mode). Here it is the host on their own list —
             * nobody gifts themselves, and the pieces are managed in the panel.
             */
            <p className="flex w-full min-h-[var(--tb-touch-min)] items-center justify-center gap-2 border border-[var(--tb-border)] px-[18px] py-[13px] text-[11px] leading-[normal] uppercase tracking-[0.1em] text-[var(--fg-subtle)]">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="shrink-0"
              >
                <rect x="5" y="11" width="14" height="10" rx="1" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              Compra bloqueada
            </p>
          ) : guestPresenting !== undefined ? (
            <button
              type="button"
              disabled={guestPresenting.isClosed === true || guestPresenting.isAnyAdding === true}
              aria-busy={guestPresenting.isAdding === true}
              onClick={() => guestPresenting.onPresent(piece)}
              className="w-full min-h-[var(--tb-touch-min)] cursor-pointer whitespace-nowrap disabled:cursor-default disabled:opacity-40 disabled:hover:bg-tb-green bg-tb-green px-[18px] py-[13px] text-[12px] leading-[normal] font-medium uppercase tracking-[0.16em] text-[var(--tb-porcelain)] transition-[background-color,color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] hover:bg-[var(--tb-green-deep)]"
            >
              {guestPresenting.isClosed === true
                ? "Lista encerrada"
                : guestPresenting.isAdding === true
                  ? "Adicionando..."
                  : "Presentear"}
            </button>
          ) : (
            <button
              type="button"
              disabled={curation === undefined && onOpenPack === undefined}
              aria-pressed={curation === undefined ? undefined : piece.inList}
              onClick={(event) =>
                curation === undefined ? onOpenPack?.(piece, event.currentTarget) : curation.onToggle(piece)
              }
              className={cn(
                "disabled:cursor-default disabled:opacity-40",
                // `leading-[normal]`: a bare `<button>` in the prototype, 44px tall; the body's 1.65 made it 48.
                "w-full min-h-[var(--tb-touch-min)] cursor-pointer whitespace-nowrap border px-[18px] py-[13px] text-[12px] leading-[normal] font-medium uppercase tracking-[0.16em] transition-[background-color,color] duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]",
                // `hover:enabled:`, never a bare `hover:`: on a closed list this button is
                // disabled, and a disabled control must not answer the pointer (ruling of
                // 2026-10-06). Same guard the vendored `ui/pagination.tsx` carries.
                piece.inList
                  ? "border-tb-green bg-tb-green text-[var(--tb-porcelain)] hover:enabled:bg-[var(--tb-green-deep)]"
                  : "border-[var(--tb-border-strong)] bg-transparent text-[var(--fg)] hover:enabled:border-[var(--fg)] hover:enabled:bg-[var(--fg)] hover:enabled:text-[var(--tb-porcelain)]",
              )}
            >
              {pieceActionLabel(curation !== undefined ? "curation" : "management", piece)}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * The piece's progress bar, scaled in **units**.
 *
 * Overshooting the goal keeps growing the bar in gilt instead of bursting it, and the goal
 * marker recedes proportionally to show where it landed.
 */
function PieceProgress({ piece }: { piece: ListedPiece }) {
  const base = Math.max(piece.purchased, piece.total) || 1;
  const excess = Math.max(0, piece.purchased - piece.total);
  const hasMetGoal = piece.purchased >= piece.total;

  const greenWidth = Math.round((Math.min(piece.purchased, piece.total) / base) * 100);
  const giltWidth = Math.round((excess / base) * 100);
  const goalLeft = Math.round((piece.total / base) * 100);

  const label = hasMetGoal
    ? `meta ${piece.total} · `
    : `${piece.purchased} de ${piece.total} · faltam ${piece.total - piece.purchased}`;

  return (
    <>
      <span className="relative mt-3 flex h-[18px] items-center">
        <span
          aria-hidden="true"
          className="absolute left-0 right-0 top-[calc(50%-2.5px)] flex h-[5px] overflow-hidden bg-tb-porcelain"
        >
          <span
            className="block h-full bg-tb-green transition-[width] duration-[600ms] ease-[var(--tb-ease-out)]"
            style={{ width: `${greenWidth}%` }}
          />
          <span
            className="block h-full bg-[var(--tb-gilt)] transition-[width] duration-[600ms] ease-[var(--tb-ease-out)]"
            style={{ width: `${giltWidth}%` }}
          />
        </span>

        {hasMetGoal ? (
          <span
            aria-hidden="true"
            style={{ left: `${goalLeft}%` }}
            className="absolute grid size-[18px] -translate-x-1/2 place-content-center rounded-full bg-[var(--tb-gilt)] text-tb-white shadow-[0_0_0_2px_#fff]"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        ) : null}
      </span>

      <span className="mt-2 font-mono text-[12px] tracking-[0.05em] text-[var(--fg-subtle)]">
        {label}
        {hasMetGoal ? (
          <span className="text-[var(--tb-gilt)]">{piece.purchased} presenteados</span>
        ) : null}
      </span>
    </>
  );
}
